import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import intersect from '@alpinejs/intersect';
import 'htmx.org';
import QRCode from 'qrcode';
import { reisassistent } from './reisassistent.js';
import { dashboardEditor } from './dashboard-editor.js';
import { dossierOverviewEditor } from './dossier-overview-editor.js';
import { groeiCharts } from './metrics-charts.js';
import { gewichtChart } from './gewicht-chart.js';
import { ftpChart } from './ftp-chart.js';
import { typeDistChart, monthlyChart, trendLine, raceDistChart } from './tijdlijn-stats-charts.js';
import { budgetStatsCharts } from './budget-stats-charts.js';
import { prijzengeldCharts } from './prijzengeld-charts.js';
import { trainingWeekChart, trainingGewichtChart, trainingKoolhydraatChart } from './training-overview-charts.js';
import { inschrijvingenChart } from './inschrijvingen-chart.js';
import { zweettestScatterChart, zweettestNatriumChart } from './zweettest-charts.js';
import { bodycompTijdChart, bodycompComboChart, bodycompScatterChart, bodycompGrafiekOpties } from './bodycomp-charts.js';
import { bloedtestTrendChart } from './bloedtest-charts.js';
import { allotmentReportCharts, allotmentProgressieChart } from './allotment-report-charts.js';
import { agendaKalender } from './agenda-kalender.js';
import { specialiteitenRadar, specialiteitEnkelTab } from './specialiteiten-radar.js';
import './stores/ai-credits.js';
import './stores/dossier-nav.js';
import './stores/nav-collapsed.js';

Alpine.plugin(collapse);
Alpine.plugin(intersect);
window.Alpine = Alpine;

// Suppress Alpine x-show style TypeErrors on non-standard nodes (SVG icons
// etc. where .style is not a full CSSStyleDeclaration). These are cosmetic
// console errors that don't affect UI — Alpine still toggles the elements.
function _isAlpineStyleErr(m) {
  return m && (m.includes('style.setProperty is not a function') ||
               m.includes('style.removeProperty is not a function'));
}
window.addEventListener('error', e => { if (_isAlpineStyleErr(e.message)) e.preventDefault(); }, true);
window.addEventListener('unhandledrejection', e => { if (_isAlpineStyleErr(e.reason?.message)) e.preventDefault(); });

// PAX-7902 — forward uncaught JS errors to Paperclip as issues (local dev only)
(function () {
  if (document.querySelector('meta[name="x-pax-env"]')?.content !== 'local') return;
  const _seen = new Set();
  function _reportJsError(message, source, lineno, colno, stack) {
    const key = (source + ':' + lineno + ':' + message).slice(0, 200);
    if (_seen.has(key)) return;
    _seen.add(key);
    fetch('/dev/js-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, source, lineno, colno, stack, url: window.location.href }),
      keepalive: true,
    }).catch(() => {});
  }
  window.addEventListener('error', function (e) {
    if (!e.error) return; // resource load failure, not a script error
    if (_isAlpineStyleErr(e.message)) return;
    _reportJsError(e.message || '', e.filename || '', e.lineno || 0, e.colno || 0, e.error?.stack || '');
  }, true);
  window.addEventListener('unhandledrejection', function (e) {
    const reason = e.reason;
    const msg = reason?.message || String(reason) || 'Unhandled Promise rejection';
    if (_isAlpineStyleErr(msg)) return;
    _reportJsError(msg, '', 0, 0, reason?.stack || '');
  });
}());

// Toast manager
document.addEventListener('alpine:init', () => {
  Alpine.data('reisassistent', reisassistent);
  Alpine.data('dashboardEditor', dashboardEditor);
  Alpine.data('dossierOverviewEditor', dossierOverviewEditor);
  Alpine.data('groeiCharts', groeiCharts);
  Alpine.data('gewichtChart', gewichtChart);
  Alpine.data('ftpChart', ftpChart);
  Alpine.data('typeDistChart', typeDistChart);
  Alpine.data('monthlyChart', monthlyChart);
  Alpine.data('trendLine', trendLine);
  Alpine.data('raceDistChart', raceDistChart);
  Alpine.data('budgetStatsCharts', budgetStatsCharts);
  Alpine.data('prijzengeldCharts', prijzengeldCharts);
  Alpine.data('trainingWeekChart', trainingWeekChart);
  Alpine.data('trainingGewichtChart', trainingGewichtChart);
  Alpine.data('trainingKoolhydraatChart', trainingKoolhydraatChart);
  Alpine.data('inschrijvingenChart', inschrijvingenChart);
  Alpine.data('zweettestScatterChart', zweettestScatterChart);
  Alpine.data('zweettestNatriumChart', zweettestNatriumChart);
  Alpine.data('bodycompTijdChart', bodycompTijdChart);
  Alpine.data('bodycompComboChart', bodycompComboChart);
  Alpine.data('bodycompScatterChart', bodycompScatterChart);
  Alpine.data('bodycompGrafiekOpties', bodycompGrafiekOpties);
  Alpine.data('bloedtestTrendChart', bloedtestTrendChart);
  Alpine.data('allotmentReportCharts', allotmentReportCharts);
  Alpine.data('allotmentProgressieChart', allotmentProgressieChart);
  Alpine.data('agendaKalender', agendaKalender);
  Alpine.data('specialiteitenRadar', specialiteitenRadar);
  Alpine.data('specialiteitEnkelTab', specialiteitEnkelTab);
  Alpine.data('toastManager', () => ({
    toasts: [],
    add({ message, type = 'info' }) {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, message, type, visible: true });
      setTimeout(() => this.remove(id), 5000);
    },
    remove(id) {
      const t = this.toasts.find(t => t.id === id);
      if (t) t.visible = false;
      setTimeout(() => { this.toasts = this.toasts.filter(t => t.id !== id); }, 300);
    },
  }));

  // x-autogrow: textarea groeit mee met de inhoud.
  // Luistert naar 'autogrow:refresh' op document zodat een textarea die in een
  // verborgen tab/paneel zit alsnog correct opmeet zodra die zichtbaar wordt.
  Alpine.directive('autogrow', (el) => {
    el.style.resize = 'none';
    el.style.overflowY = 'hidden';
    let min = 0;
    const resize = () => {
      if (el.offsetParent === null) return; // verborgen (bv. inactieve tab)
      if (!min) min = el.offsetHeight;      // bewaar de rows-hoogte als ondergrens
      el.style.height = 'auto';
      el.style.height = Math.max(min, el.scrollHeight) + 'px';
    };
    el.addEventListener('input', resize);
    document.addEventListener('autogrow:refresh', resize);
    resize();
  });
});

// Na Alpine-init alle autogrow-textareas opmeten (x-show is dan toegepast).
document.addEventListener('alpine:initialized', () => {
  requestAnimationFrame(() => document.dispatchEvent(new CustomEvent('autogrow:refresh')));
});

Alpine.start();

// HTMX: attach CSRF + X-Requested-With
document.body.addEventListener('htmx:configRequest', (e) => {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
  if (csrf) e.detail.headers['X-CSRF-Token'] = csrf;
  e.detail.headers['X-Requested-With'] = 'HTMX';
});

// Tabel-scroll: wikkelt alle tabellen in een overflow-x-auto container
// zodat ze zowel op mobile als desktop horizontaal scrollen.
// Loopt opnieuw op htmx:afterSettle voor HTMX-geladen content.
function wrapTablesIn(root) {
  root.querySelectorAll('table').forEach(table => {
    if (table.closest('[data-table-scroll]')) return; // al gewrapped
    if (table.closest('.mce-content-body')) return;   // TinyMCE editor
    const parent = table.parentElement;
    if (!parent) return;
    const ox = window.getComputedStyle(parent).overflowX;
    if (ox === 'auto' || ox === 'scroll') return;     // al scrollbaar

    const wrapper = document.createElement('div');
    wrapper.className = 'overflow-x-auto';
    wrapper.setAttribute('data-table-scroll', '');
    parent.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
}
document.addEventListener('DOMContentLoaded', () => wrapTablesIn(document));
document.body.addEventListener('htmx:afterSettle', e => wrapTablesIn(e.target));

// MFA QR code: render canvas in #mfa-qr element
document.addEventListener('DOMContentLoaded', () => {
  const el = document.getElementById('mfa-qr');
  if (el?.dataset.uri) {
    QRCode.toCanvas(el, el.dataset.uri, { width: 200, margin: 1 }, (err) => {
      if (!err) el.querySelector('span')?.remove();
    });
  }
});

// X-Toast response header handler (HTMX responses)
document.body.addEventListener('htmx:afterRequest', (e) => {
  const toast = e.detail.xhr.getResponseHeader('X-Toast');
  if (toast) {
    try {
      window.dispatchEvent(new CustomEvent('toast', { detail: JSON.parse(toast) }));
    } catch {
      window.dispatchEvent(new CustomEvent('toast', { detail: { message: toast, type: 'info' } }));
    }
  }
});

// Tenant notifications polling
document.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector('meta[name="csrf-token"]')) return;
  if (document.getElementById('master-notif-poller')) return;

  let since = 0;
  let intervalId = null;
  // De eerste poll dient enkel als baseline: ze stelt `since` in op de
  // huidige laatste melding zonder een toast te tonen. Zonder dit zou elke
  // pagina-load (since=0) de meest recente support-melding opnieuw toasten.
  let firstPoll = true;

  const poll = async () => {
    try {
      const res = await fetch(`/admin/notifications/poll?since=${since}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      // Sessie verlopen → stop pollen, server stuurde 401 met HX-Refresh.
      if (res.status === 401) {
        if (intervalId) clearInterval(intervalId);
        return;
      }
      if (!res.ok) return;

      const data = await res.json();
      since = data.latest_id ?? since;

      window.dispatchEvent(new CustomEvent('update-unread', { detail: { count: data.count } }));
      window.dispatchEvent(new CustomEvent('support-unread-update', { detail: { count: data.support_unread ?? 0 } }));

      const toastHeader = res.headers.get('X-Toast');
      if (toastHeader && !firstPoll) {
        try {
          window.dispatchEvent(new CustomEvent('toast', { detail: JSON.parse(toastHeader) }));
        } catch {}
      }
      firstPoll = false;
    } catch {}
  };

  poll();
  intervalId = setInterval(poll, 30_000);
});

// Master notifications polling (alleen voor master-pagina's)
document.addEventListener('DOMContentLoaded', () => {
  const poller = document.getElementById('master-notif-poller');
  if (!poller) return;

  let since = parseInt(poller.dataset.latestId ?? '0');
  let intervalId = null;

  const poll = async () => {
    try {
      const res = await fetch(`/master/notifications/poll?since=${since}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      // Sessie verlopen → stop pollen, server stuurde 401 met HX-Refresh.
      if (res.status === 401) {
        if (intervalId) clearInterval(intervalId);
        return;
      }
      if (!res.ok) return;

      const data = await res.json();
      since = data.latest_id ?? since;

      window.dispatchEvent(new CustomEvent('update-master-unread', { detail: { count: data.count } }));

      const toastHeader = res.headers.get('X-Toast');
      if (toastHeader) {
        try {
          window.dispatchEvent(new CustomEvent('toast', { detail: JSON.parse(toastHeader) }));
        } catch {}
      }
    } catch {}
  };

  // Start pas na 30s (pagina-load toont al actuele count)
  setTimeout(() => {
    poll();
    intervalId = setInterval(poll, 30_000);
  }, 30_000);
});

// Formulier-toegankelijkheid (EAA 2025): bij een server-gevalideerde fout de
// focus naar het probleem verplaatsen zodat screenreader-/toetsenbordgebruikers
// meteen weten waar het misging. Voorkeur gaat naar het eerste ongeldige veld
// (aria-invalid) zodat de gebruiker direct kan corrigeren; is dat er niet, dan
// naar de foutsamenvatting (role="alert"). Werkt op elke publieke/website-form
// omdat de fouten na een POST via een volledige pagina-reload terugkomen.
document.addEventListener('DOMContentLoaded', () => {
  // Enkel op pagina's met een formulier; toasts gebruiken aria-live="polite"
  // (geen role="alert"), dus een role="alert" hier is altijd een foutmelding.
  if (!document.querySelector('form')) return;

  const invalid = document.querySelector('[aria-invalid="true"]');
  const target = invalid || document.querySelector('[role="alert"]');
  if (!target) return;

  // De samenvatting is normaal niet focusbaar; maak hem tijdelijk focusbaar
  // zonder hem in de normale tab-volgorde te plaatsen.
  if (!invalid && !target.hasAttribute('tabindex')) {
    target.setAttribute('tabindex', '-1');
  }

  requestAnimationFrame(() => {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    try { target.focus({ preventScroll: true }); } catch {}
  });
});
